import React, { useState } from 'react';
import { CloudOff, RefreshCw } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { getActiveProfile } from '../../../data/supabase/session';
import { syncLinkedChildren } from '../../../data/supabase/syncLinkedChildren';
interface NoChildDataNoticeProps {
  childName: string;
  /** Wat deze specifieke pagina zou tonen zodra er wél gegevens zijn. */
  whatAppearsHere: string;
}

/**
 * "Je kind is gekoppeld, maar er is nog geen voortgang om te tonen."
 *
 * ── Waarom dit bestaat ────────────────────────────────────────────────────
 *
 * De koppeling (`parent_links`) en de oefengegevens (`solo_progress`) zijn
 * twee verschillende bronnen. De parent-pagina's behandelden een lege
 * `learnerData` als "geen kind gekoppeld": Home toonde het koppelformulier,
 * Vakken zei "Nog geen kind gekoppeld". Beide zijn aantoonbaar onwaar op het
 * moment dat de naam van het kind elders in datzelfde portaal op het scherm
 * staat, en ze sturen de ouder naar de verkeerde handeling: nóg een keer
 * koppelen, met een code die hij misschien niet meer heeft.
 *
 * Dit is de derde toestand die ontbrak. Niet "leeg" en niet "kapot", maar
 * "hier is nog niets van te zien" — met de naam van het kind erin, want dat
 * is het bewijs dat de koppeling wél werkte.
 *
 * ── CORRECTIE (aug 2026): "op dit apparaat" was onjuist ───────────────────
 *
 * De eerste versie schreef dat er "nog geen oefenresultaten op dit apparaat"
 * stonden, en liet om diezelfde reden een verversknop weg: gegevens zouden
 * ontstaan doordat het kind oefent, niet doordat de ouder ververst.
 *
 * Dat klopte niet meer. `fetchProgressForLearners()` haalt de voortgang uit
 * `solo_progress` op de SERVER, en sinds 0016 mag een gekoppelde ouder die
 * rij lezen. De voortgang is dus apparaat-onafhankelijk — precies zoals het
 * hoort, want papa en mama kijken elk op hun eigen telefoon.
 *
 * Die ene onjuiste zin trok twee dingen scheef:
 *
 *   1. Ze suggereerde dat de ouder op het verkeerde toestel keek, en dus dat
 *      overstappen naar de tablet van het kind zou helpen. Dat helpt niet —
 *      daar kan de ouder niet eens bij zijn eigen portaal.
 *   2. Ze rechtvaardigde het weglaten van de verversknop. Terwijl verversen
 *      hier juist wél iets oplevert: het kind kan een kwartier geleden
 *      geoefend hebben op zijn eigen tablet, ná het moment dat dit scherm
 *      zijn gegevens ophaalde.
 *
 * Wat er wél aan de hand kan zijn, in volgorde van waarschijnlijkheid:
 *   - het kind heeft nog niet geoefend sinds de koppeling;
 *   - het kind heeft wél geoefend, maar zijn toestel heeft nog niet
 *     gesynchroniseerd (het wegschrijven wacht op een stille periode);
 *   - het kind werkt zonder serveraccount, en dan blijft zijn voortgang
 *     inderdaad op zijn eigen apparaat.
 *
 * De tekst noemt de eerste twee, want dat zijn de gevallen waarin wachten of
 * verversen daadwerkelijk helpt. Het derde geval is zeldzaam en niet door de
 * ouder op te lossen; dat benoemen zou hem laten zoeken naar een instelling
 * die hij niet heeft.
 */
export function NoChildDataNotice({
  childName,
  whatAppearsHere
}: NoChildDataNoticeProps) {
  const [status, setStatus] = useState<'idle' | 'busy' | 'done'>('idle');
  const handleRefresh = async () => {
    const profile = getActiveProfile();
    if (!profile?.id) return;
    setStatus('busy');
    await syncLinkedChildren(profile.id);
    // Lukt het ophalen, dan verdwijnt dit hele paneel doordat de store nu wél
    // voortgang bevat. Blijft het staan, dan is er echt niets — en dan is
    // 'done' de eerlijke uitkomst: gecontroleerd, niets gevonden.
    setStatus('done');
  };
  return <Card className="p-8 border-slate-200 bg-white text-center max-w-lg mx-auto" data-id="element-8461" data-mp="aarlt2kxas5t">
      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8462" data-mp="acks2grmjada">
        <CloudOff className="w-6 h-6 text-slate-400" aria-hidden="true" data-id="element-8463" data-mp="3dzuuzyn9pqpj" />
      </div>
      <h2 className="text-xl font-black text-slate-900 mb-2" data-id="element-8464" data-mp="3dxru2jkkud61">
        Nog geen oefengegevens van {childName}
      </h2>
      <p className="text-slate-600 leading-relaxed" data-id="element-8465" data-mp="1fi17loc319gt">
        De koppeling is gelukt — {childName} staat bij je account. Er is alleen
        nog geen voortgang om te laten zien. {whatAppearsHere}
      </p>
      <p className="text-sm text-slate-500 leading-relaxed mt-3" data-id="element-8466" data-mp="1oiz9o12yn6wr">
        Heeft {childName} net geoefend? Het kan een paar minuten duren voordat
        dat is doorgegeven.
      </p>
      <button type="button" onClick={handleRefresh} disabled={status === 'busy'} className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none" data-id="element-8467" data-mp="239g8dco7nipy">
        <RefreshCw className={`w-4 h-4 ${status === 'busy' ? 'animate-spin' : ''}`} aria-hidden="true" data-id="element-8468" data-mp="huslps865yea" />
        {status === 'busy' ? 'Bezig met ophalen…' : 'Opnieuw ophalen'}
      </button>
      {status === 'done' && <p className="text-sm text-slate-500 mt-3" role="status" data-id="element-8469" data-mp="2okm937qk9nac">
          Nog niets gevonden. Probeer het later nog eens.
        </p>}
    </Card>;
}